import moment from 'moment'
import { getDocs, getRegs } from './fc'
import oikosApi from '../services/oikos'

const asList = (x) => (Array.isArray(x) ? x : [])

/** Projeto da receita: rec.orcRubr_id → proj.orcRubr_id (mesmo critério do e-mail da NFS-e). */
export async function getProjIdFromRec(rec) {
  const orc = Number(rec?.orcRubr_id)
  if (!(orc > 0)) return 0
  const projs = asList(await getRegs('proj', { orcRubr_id: orc }))
    .map((p) => Number(p?.proj_id) || 0)
    .filter((id) => id > 0)
    .sort((a, b) => b - a)
  return projs[0] || 0
}

export function isSupportDocVigente(doc, refDate = moment()) {
  if (!doc) return false
  if (doc.documento_ativo != null && Number(doc.documento_ativo) === 0) return false
  const fim = doc.documento_fimVig
  if (!fim) return false
  return moment(fim).startOf('day').isSameOrAfter(moment(refDate).startOf('day'))
}

const tipoLabel = (types, tpId) => {
  const tp = types.find((t) => Number(t.tpDocumento_id) === Number(tpId))
  return tp?.tpDocumento_desc || (tpId ? `Tipo ${tpId}` : 'Documento')
}

/**
 * Documentos escolhidos no processo de faturamento da aba Eventos de pagamento
 * (documentoVinc tabRef projFat, tabId = proj_id).
 */
export async function loadFatSupportStatus({ projId }) {
  const id = Number(projId)
  if (!(id > 0)) return []

  const [vincs, files, types] = await Promise.all([
    getRegs('documentoVinc', {
      documentoVinc_tabRef: 'projFat',
      documentoVinc_tabId: id,
    }),
    getDocs('projFat', id),
    getRegs('tpDocumento', {}, {
      tpDocumento_id: 'tpDocumento_id',
      tpDocumento_desc: 'tpDocumento_desc',
    }),
  ])

  const typeRows = asList(types)
  const filesByDoc = new Map()
  asList(files).forEach((d) => {
    const docId = Number(d.documento_id)
    if (docId > 0 && !filesByDoc.has(docId)) filesByDoc.set(docId, d)
  })

  const seen = new Set()
  const rows = []
  for (const v of asList(vincs)) {
    const docId = Number(v.documento_id)
    if (!(docId > 0) || seen.has(docId)) continue
    seen.add(docId)
    const withFile = filesByDoc.get(docId) || null
    let doc = withFile
    if (!doc) {
      const regs = await getRegs('documento', { documento_id: docId })
      doc = asList(regs)[0] || null
    }
    const hasFile = !!(withFile && (withFile.arquivo_caminho || withFile.arquivo_id))
    let status = 'missing'
    if (doc && hasFile && isSupportDocVigente(doc)) status = 'ok'
    else if (doc && hasFile) status = 'expired'
    const tipo = tipoLabel(typeRows, doc?.tpDocumento_id)
    const desc = String(doc?.documento_desc || '').trim()
    rows.push({
      key: `doc-${docId}`,
      documento_id: docId,
      documentoVinc_id: Number(v.documentoVinc_id) || Number(withFile?.documentoVinc_id) || null,
      tpDocumento_id: doc?.tpDocumento_id != null ? Number(doc.tpDocumento_id) : null,
      label: desc ? `${tipo} — ${desc}` : tipo,
      status,
      hasFile,
      arquivo_caminho: withFile?.arquivo_caminho || doc?.arquivo_caminho || null,
      documento_fimVig: doc?.documento_fimVig || null,
      doc,
    })
  }
  return rows
}

export function summarizeFatSupport(rows) {
  const list = asList(rows)
  if (!list.length) return 'empty'
  if (list.every((r) => r.status === 'ok')) return 'ok'
  if (list.some((r) => r.status === 'missing')) return 'missing'
  return 'expired'
}

const supportCache = new Map()

export function invalidateFatSupportCache(orcRubrId) {
  const orc = Number(orcRubrId)
  if (orc > 0) supportCache.delete(orc)
  else supportCache.clear()
}

export async function loadFatSupportForRec(rec) {
  const orc = Number(rec?.orcRubr_id) || 0
  if (orc > 0 && supportCache.has(orc)) return supportCache.get(orc)
  const projId = await getProjIdFromRec(rec)
  const rows = projId > 0 ? await loadFatSupportStatus({ projId }) : []
  const packed = {
    projId,
    rows,
    summary: summarizeFatSupport(rows),
    orcRubr_id: orc,
  }
  if (orc > 0) supportCache.set(orc, packed)
  return packed
}

export async function desvincularProjFat(documentoVincId) {
  const id = Number(documentoVincId)
  if (!(id > 0)) return
  await oikosApi.post('/', {
    action: 'delete',
    table: 'documentoVinc',
    data: { documentoVinc_id: id },
  })
}
